import { CompanyIntegrationStatus } from "@/components/integrations/company/company-integrations-sync";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CompanyCrmIntegration,
  CrmIntegrationProviderSupported,
} from "@shaarp/common";
import { MdInsertLink } from "react-icons/md";

type SyncedIntegrationsProps = {
  integrations: CompanyIntegrationStatus[];
  onSync: (integrationKey: CrmIntegrationProviderSupported) => void;
  onOpenExternal: (crmCompany: CompanyCrmIntegration) => void;
};

/**
 * Each row carries its own busy state, so refreshing one CRM leaves the other
 * row idle and clickable.
 *
 * Each row opens *its own* CRM record: the link is read from the integration it
 * belongs to, not from a single shared one, so with two CRMs connected the
 * HubSpot row can no longer send the user to the Seliaris record.
 */
export const SyncedIntegrations = ({
  integrations,
  onSync,
  onOpenExternal,
}: SyncedIntegrationsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="px-3 py-2">
        <div className="text-sm font-medium text-foreground">
          {t("company.integrations.syncedIntegrations")}
        </div>
      </div>

      {integrations.map((integration) => (
        <div
          key={integration.key}
          className="flex gap-4 items-center p-2 rounded-sm [&_svg]:size-4 [&_svg]:shrink-0"
        >
          <img
            src={integration.logo}
            alt={integration.name}
            className="object-contain shrink-0 w-6 h-6"
          />
          <div className="flex flex-col flex-1 gap-1">
            <span className="text-sm font-medium">{integration.name}</span>
            <div className="flex gap-2 items-center">
              {!integration.isLoading && (
                <MdInsertLink className="w-5 h-5 text-success" />
              )}
              <span className="text-xs text-muted-foreground">
                {integration.isLoading
                  ? t("company.integrations.syncing")
                  : t("company.integrations.synced")}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-2 items-center">
            <button
              className="flex justify-center items-center p-0 w-8 h-8 rounded-md hover:bg-muted"
              disabled={!integration.crmRecord?.directLink}
              onClick={(e) => {
                e.stopPropagation();
                if (integration.crmRecord) {
                  onOpenExternal({
                    id: integration.crmRecord.id,
                    directLink: integration.crmRecord.directLink,
                  });
                }
              }}
              title={t("company.integrations.openInIntegration", {
                integration: integration.name,
              })}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </button>
            <button
              className="flex justify-center items-center p-0 w-8 h-8 rounded-md hover:bg-muted"
              disabled={integration.isLoading}
              onClick={(e) => {
                e.stopPropagation();
                onSync(integration.key);
              }}
              title={t("company.integrations.syncToIntegration", {
                integration: integration.name,
              })}
            >
              <svg
                className={`w-4 h-4 ${integration.isLoading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </>
  );
};
